import type { Experiment } from 'types';
import { useQuery } from '@tanstack/react-query';
import { useClient } from 'hooks/useClient';
import { useWebsites } from '@/state/useWebsites';

type SurveyExperimentOptionsParams = {
  /**
   * The survey's own website. Falls back to the website selected in the header,
   * which is right only for a survey not created yet: an existing survey opened
   * from a link can belong to another website than the one selected, and listing
   * that website's experiments would show its real link as gone and offer links
   * the API refuses.
   */
  websiteId?: number | null;
  /** False until the list is needed, so screens that may never show it do not fetch it. */
  enabled?: boolean;
};

/**
 * The website's experiments a survey can be tied to, with their variants.
 *
 * Same key and request as the pipeline board's, so the two share one cache entry
 * and every invalidateQueries(['EXPERIMENTS', websiteId]) refreshes both. Archived
 * experiments are left out: the API refuses to link one.
 */
export const useSurveyExperimentOptions = ({ websiteId, enabled = true }: SurveyExperimentOptionsParams = {}) => {
  const apiClient = useClient();
  const { website } = useWebsites();
  const scope = websiteId ?? website?.id ?? '';

  const { data, isLoading } = useQuery<Experiment[]>({
    queryKey: ['EXPERIMENTS', scope],
    enabled: enabled && !!scope,
    queryFn: () => apiClient.get<Experiment[]>(`/websites/${scope}/experiments`),
  });

  const experiments = (data ?? []).filter((experiment) => !experiment.archived);
  return { experiments, isLoading };
};
